import React from "react"
import { useQuery } from "@tanstack/react-query"
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Line,
  LineChart,
  Cell,
  PieChart,
  Pie,
  ResponsiveContainer,
} from "recharts"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card"
import { Skeleton } from "../components/ui/skeleton"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../components/ui/chart"
import {
  Utensils,
  Users,
  TrendingUp,
  Camera,
  MessageSquare,
  Search,
  Barcode,
  Star,
  Plus,
  Zap,
  Calendar,
} from "lucide-react"
import api from "../services/api"
import { useDashboardFilters } from "../state/dashboardFilters"

// Source display config
const sourceConfig = {
  photo_ai: { label: "Photo AI", color: "hsl(var(--chart-1))", icon: Camera },
  describe_ai: { label: "Describe AI", color: "hsl(var(--chart-2))", icon: MessageSquare },
  database: { label: "Database", color: "hsl(var(--chart-3))", icon: Search },
  barcode: { label: "Barcode", color: "hsl(var(--chart-4))", icon: Barcode },
  favorite: { label: "Favorites", color: "hsl(var(--chart-5))", icon: Star },
  custom_food: { label: "Custom Food", color: "hsl(142 76% 36%)", icon: Plus },
  custom_meal: { label: "Custom Meal", color: "hsl(262 83% 58%)", icon: Utensils },
  quick_add: { label: "Quick Add", color: "hsl(38 92% 50%)", icon: Zap },
  recent: { label: "Recent", color: "hsl(199 89% 48%)", icon: Calendar },
  manual: { label: "Manual", color: "hsl(0 0% 45%)", icon: Plus },
}

// Meal type display config
const mealTypeConfig = {
  breakfast: { label: "Breakfast", color: "hsl(38 92% 50%)" },
  lunch: { label: "Lunch", color: "hsl(142 76% 36%)" },
  dinner: { label: "Dinner", color: "hsl(262 83% 58%)" },
  snacks: { label: "Snacks", color: "hsl(var(--chart-4))" },
  preWorkout: { label: "Pre-Workout", color: "hsl(var(--chart-1))" },
  postWorkout: { label: "Post-Workout", color: "hsl(var(--chart-2))" },
  other: { label: "Other", color: "hsl(0 0% 45%)" },
}

// Stat Card component
function StatCard({ title, value, icon: Icon, isLoading, description }) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground truncate">
              {title}
            </p>
            {isLoading ? (
              <Skeleton className="h-8 w-24 mt-2" />
            ) : (
              <p className="text-2xl font-bold mt-2 tabular-nums">{value}</p>
            )}
            {description && !isLoading && (
              <p className="text-xs text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          {Icon && (
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 ml-4">
              <Icon className="h-5 w-5 text-primary" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default function NutritionPage() {
  const { adminOnly, autoRefresh } = useDashboardFilters()

  const { data: stats, isLoading } = useQuery({
    queryKey: ["foodLogStats", adminOnly],
    queryFn: () => api.getFoodLogStats(adminOnly),
    refetchInterval: autoRefresh ? 30000 : false,
  })

  // Transform source data for chart
  const sourceChartData = React.useMemo(() => {
    if (!stats?.countsBySource) return []
    return Object.entries(stats.countsBySource)
      .map(([source, count]) => ({
        source,
        count,
        label: sourceConfig[source]?.label || source,
        fill: sourceConfig[source]?.color || "hsl(var(--chart-1))",
      }))
      .sort((a, b) => b.count - a.count)
  }, [stats])

  // Transform meal type data for chart
  const mealTypeChartData = React.useMemo(() => {
    if (!stats?.countsByMealType) return []
    return Object.entries(stats.countsByMealType)
      .map(([mealType, count]) => ({
        mealType,
        count,
        label: mealTypeConfig[mealType]?.label || mealType,
        fill: mealTypeConfig[mealType]?.color || "hsl(var(--chart-1))",
      }))
      .sort((a, b) => b.count - a.count)
  }, [stats])

  // Transform daily trend data
  const dailyTrendData = React.useMemo(() => {
    if (!stats?.dailyTrend) return []
    return stats.dailyTrend.map((d) => ({
      ...d,
      displayDate: formatDate(d.date),
    }))
  }, [stats])

  // Transform calorie bucket data
  const calorieBucketData = React.useMemo(() => {
    if (!stats?.countsByCalorieBucket) return []
    const order = ["0-99", "100-249", "250-499", "500-999", "1000+"]
    return order
      .filter((bucket) => stats.countsByCalorieBucket[bucket] !== undefined)
      .map((bucket) => ({
        bucket,
        count: stats.countsByCalorieBucket[bucket] || 0,
      }))
  }, [stats])

  function formatDate(dateString) {
    if (!dateString) return ""
    try {
      const date = new Date(dateString)
      return `${date.getMonth() + 1}/${date.getDate()}`
    } catch {
      return dateString
    }
  }

  // Find top source
  const topSource = sourceChartData[0]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Nutrition Analytics</h1>
        <p className="text-muted-foreground">
          Track food logging usage patterns and source distribution.
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Food Logs"
          value={stats?.totalLogs?.toLocaleString() || "0"}
          icon={Utensils}
          isLoading={isLoading}
          description={`${stats?.logsToday || 0} today`}
        />
        <StatCard
          title="This Week"
          value={stats?.logsThisWeek?.toLocaleString() || "0"}
          icon={TrendingUp}
          isLoading={isLoading}
        />
        <StatCard
          title="Unique Users"
          value={stats?.uniqueUsers?.toLocaleString() || "0"}
          icon={Users}
          isLoading={isLoading}
        />
        <StatCard
          title="Avg Logs/User"
          value={stats?.avgLogsPerUser || "0"}
          icon={Utensils}
          isLoading={isLoading}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Logs by Source
            </CardTitle>
            <CardDescription>
              {topSource && `Top: ${topSource.label} (${((topSource.count / (stats?.totalLogs || 1)) * 100).toFixed(1)}%)`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : sourceChartData.length === 0 ? (
              <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <ChartContainer config={sourceConfig} className="h-[300px] w-full">
                <BarChart data={sourceChartData} layout="vertical" accessibilityLayer>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" tickLine={false} axisLine={false} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    width={100}
                  />
                  <ChartTooltip
                    cursor={{ fill: "hsl(var(--muted))" }}
                    content={<ChartTooltipContent />}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {sourceChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Daily Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Daily Food Logs (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : dailyTrendData.length === 0 ? (
              <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <ChartContainer
                config={{ logs: { label: "Food Logs", color: "hsl(var(--chart-1))" } }}
                className="h-[300px] w-full"
              >
                <LineChart data={dailyTrendData} accessibilityLayer>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="displayDate"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    interval="preserveStartEnd"
                  />
                  <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
                  <ChartTooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    content={
                      <ChartTooltipContent
                        labelFormatter={(value, payload) => {
                          if (payload?.[0]?.payload?.date) {
                            const date = new Date(payload[0].payload.date)
                            return date.toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })
                          }
                          return value
                        }}
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Meal Type Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Utensils className="h-5 w-5" />
              Logs by Meal Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : mealTypeChartData.length === 0 ? (
              <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <ChartContainer config={mealTypeConfig} className="h-[300px] w-full">
                <BarChart data={mealTypeChartData} accessibilityLayer>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
                  <ChartTooltip
                    cursor={{ fill: "hsl(var(--muted))" }}
                    content={<ChartTooltipContent />}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {mealTypeChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Calorie Bucket Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Logs by Calorie Range
            </CardTitle>
            <CardDescription>
              Distribution of food entries by calorie content
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : calorieBucketData.length === 0 ? (
              <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <ChartContainer
                config={{ count: { label: "Entries", color: "hsl(var(--chart-2))" } }}
                className="h-[300px] w-full"
              >
                <BarChart data={calorieBucketData} accessibilityLayer>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="bucket" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
                  <ChartTooltip
                    cursor={{ fill: "hsl(var(--muted))" }}
                    content={<ChartTooltipContent />}
                  />
                  <Bar dataKey="count" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
